import { Box, Drawer as MuiDrawer, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import {
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type UIEvent,
} from 'react';
import { lelyTokenPrimitives } from '../../styles/newStyles/LelyMuiTheme';
import ConfirmCloseDialog from '../muiWrappers/LelyDialog/ConfirmCloseDialog';
import { SCROLL_SHADOW_UP } from '@/common/constants';
import LelyButton from '../muiWrappers/LelyButton/LelyButton';
import LelyIconButton from '../muiWrappers/LelyIconButton/LelyIconButton';
import { NAVIGATION_MENU_OFFSET } from '../muiWrappers/navigationMenuConstants';

const t = lelyTokenPrimitives as Record<string, string>;

export type DrawerAnchor = 'left' | 'right';

export interface DrawerProps {
  open: boolean;
  anchor?: DrawerAnchor;
  title: string;
  children: ReactNode;
  showBackButton?: boolean;
  showFooter?: boolean;
  invisibleBackdrop?: boolean;
  keepMounted?: boolean;
  hasUnsavedChanges?: boolean;
  disableApply?: boolean;
  onClose: () => void;
  onCloseAll?: () => void;
  onApply?: () => void;
  onContentChange?: () => void;
}

function Drawer({
  open,
  anchor,
  title,
  children,
  showBackButton,
  showFooter,
  invisibleBackdrop = false,
  keepMounted = false,
  hasUnsavedChanges = false,
  disableApply = false,
  onClose,
  onCloseAll,
  onContentChange,
  onApply,
}: DrawerProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [headerShadow, setHeaderShadow] = useState(false);
  const [footerShadow, setFooterShadow] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const BackIcon = anchor === 'left' ? ArrowForwardIcon : ArrowBackIcon;

  useLayoutEffect(() => {
    if (!open) {
      setHeaderShadow(false);
      setFooterShadow(false);
      return;
    }

    const frameId = requestAnimationFrame(() => {
      const el = contentRef.current;
      if (!el) return;
      setHeaderShadow(el.scrollTop > 0);
      setFooterShadow(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
    });

    return () => cancelAnimationFrame(frameId);
  }, [open]);

  const handleContentScroll = (event: UIEvent<HTMLDivElement>) => {
    const el = event.currentTarget;
    setHeaderShadow(el.scrollTop > 0);
    setFooterShadow(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  };

  const requestClose = () => {
    if (hasUnsavedChanges) {
      setConfirmOpen(true);
    } else {
      onClose();
    }
  };

  const handleCancelConfirm = () => {
    setConfirmOpen(false);
    onClose();
  };

  const handleCloseClick = () => {
    if (hasUnsavedChanges) {
      setConfirmOpen(true);
    } else {
      (onCloseAll ?? onClose)();
    }
  };

  const handleApply = () => {
    if (disableApply) return;

    onApply?.();
    setConfirmOpen(false);
  };

  return (
    <>
      <MuiDrawer
        anchor={anchor}
        open={open}
        onClose={requestClose}
        ModalProps={{
          keepMounted,
          BackdropProps: invisibleBackdrop ? { invisible: true } : undefined,
        }}
        PaperProps={{
          sx: (theme) => ({
            ...(anchor !== 'right' && { left: NAVIGATION_MENU_OFFSET }),
            borderRadius:
              anchor === 'right'
                ? `${theme.lelyBorderRadius.LG} 0 0 0`
                : `0 ${theme.lelyBorderRadius.LG} 0 0`,
          }),
        }}
      >
        <Box
          component="header"
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            gap: theme.lelySpacing.MD,
            alignSelf: 'stretch',
            height: theme.lelySpacing._8XL,
            px: theme.lelySpacing.LG,
            py: theme.lelySpacing.MD,
            backgroundColor: theme.palette.background.light,
            zIndex: 1,
            boxShadow: headerShadow ? t.boxShadow3xl : 'none', // TODO HERE
          })}
        >
          {showBackButton && (
            <LelyIconButton
              aria-label="Go back"
              size="md"
              onClick={requestClose}
              icon={<BackIcon />}
              sx={(theme) => ({ color: theme.palette.text.subtle })}
            />
          )}

          <Typography
            variant="h6"
            sx={(theme) => ({
              flex: 1,
              fontWeight: 'medium',
              color: theme.palette.text.default,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontFamily: theme.lelyFontFamily.NOTO,
            })}
          >
            {title}
          </Typography>

          <LelyIconButton
            size="lg"
            aria-label="Close drawer"
            onClick={handleCloseClick}
            icon={<CloseIcon />}
            sx={(theme) => ({ color: theme.palette.text.subtle })}
          />
        </Box>
        <Box
          ref={contentRef}
          onScroll={handleContentScroll}
          onChange={onContentChange}
          sx={(theme) => ({
            display: 'flex',
            flex: '1 0 0',
            overflowY: 'auto',
            flexDirection: 'column',
            alignItems: 'stretch',
            alignSelf: 'stretch',
            gap: theme.lelySpacing.SM,
            py: theme.lelySpacing.SM,
            px: theme.lelySpacing.LG,
          })}
        >
          {children}
        </Box>

        {showFooter && (
          <>
            <Stack
              component="footer"
              direction="row"
              sx={(theme) => ({
                position: 'relative',
                height: theme.lelySpacing._8XL,
                p: theme.lelySpacing.LG,
                justifyContent: 'flex-end',
                alignItems: 'flex-start',
                gap: theme.lelySpacing.LG,
                flexShrink: 0,
                zIndex: 1,
                alignSelf: 'stretch',
                backgroundColor: theme.palette.background.light,
                boxShadow: footerShadow ? SCROLL_SHADOW_UP : 'none',
              })}
            >
              <LelyButton
                variant="tertiary"
                onClick={requestClose}
                sx={{ flex: 1 }}
                data-testid={open ? 'lely-drawer-cancel-button' : undefined}
              >
                Cancel
              </LelyButton>
              <LelyButton
                variant="primary"
                onClick={handleApply}
                disabled={disableApply}
                sx={{ flex: 1 }}
                data-testid={open ? 'lely-drawer-apply-button' : undefined}
              >
                Apply
              </LelyButton>
            </Stack>
          </>
        )}
      </MuiDrawer>

      <ConfirmCloseDialog
        open={confirmOpen}
        onApplyClick={handleApply}
        onCancelClick={handleCancelConfirm}
        onClose={() => setConfirmOpen(false)}
      />
    </>
  );
}

export default Drawer;
